import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { Text } from '../text';
import { useClients, useContracts, useAfianzadoras } from '../../contexts/DataContext';
import {
  ShieldCheckIcon,
  CurrencyDollarIcon,
  UserIcon,
  HomeModernIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/solid';
import { getEmisorNombre } from '../../utils/calculations';
import { FianzaView } from '../FianzaView';

const formatMXN = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

// The per-afianzadora cards of the client portal (línea de afianzamiento,
// fianzas activas, obligados solidarios, BI en garantía). Extracted from
// ClientPortalAfianzadoras so the Empresa page's Afianzadoras tab and the
// legacy standalone page render the same thing.
const PortalAfianzadorasList = ({ clients }) => {
  const { clientsById } = useClients();
  const { contractsList } = useContracts();
  const { afianzadorasById, afianzadorasLoading } = useAfianzadoras();
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);

  // Fetch afianzadoras subcollections for each client
  const [clientAfianzadorasMap, setClientAfianzadorasMap] = useState({});
  const [osMap, setOsMap] = useState({});
  const [biMap, setBiMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clients.length === 0) return;
    const unsubs = [];

    clients.forEach((client) => {
      // Afianzadoras subcollection
      const afUnsub = onSnapshot(
        collection(db, 'clientes', client.id, 'afianzadoras'),
        (snap) => {
          const data = snap.docs.map((d) => ({ id: d.id, clientId: client.id, ...d.data() }));
          setClientAfianzadorasMap((prev) => ({ ...prev, [client.id]: data }));
          setLoading(false);
        }
      );
      unsubs.push(afUnsub);

      // Obligados solidarios
      const osUnsub = onSnapshot(
        collection(db, 'clientes', client.id, 'obligados_solidarios'),
        (snap) => {
          setOsMap((prev) => {
            const next = { ...prev };
            snap.docs.forEach((d) => { next[d.id] = { id: d.id, clientId: client.id, ...d.data() }; });
            return next;
          });
        }
      );
      unsubs.push(osUnsub);

      // Bienes inmuebles
      const biUnsub = onSnapshot(
        collection(db, 'clientes', client.id, 'bienes_inmuebles'),
        (snap) => {
          setBiMap((prev) => {
            const next = { ...prev };
            snap.docs.forEach((d) => { next[`${client.id}/${d.id}`] = { id: d.id, clientId: client.id, ...d.data() }; });
            return next;
          });
        }
      );
      unsubs.push(biUnsub);
    });

    return () => unsubs.forEach((u) => u());
  }, [clients]);

  // Map fianzas to their afianzadora (by emisor name — same logic as ClientAfianzadoras)
  const fianzasByAfianzadoraId = useMemo(() => {
    const now = Date.now();
    const clientIds = new Set(clients.map((c) => c.id));
    const map = {};

    // Collect all active fianzas
    const activeFianzas = [];
    contractsList.forEach((contrato) => {
      if (!clientIds.has(contrato.clientId)) return;
      (contrato.fianzas || []).forEach((fianza) => {
        const fin = fianza.finVigenciaFianza || fianza.xmlFianza?.finVigenciaFianza;
        if (fin) {
          const finTs = typeof fin === 'number' ? fin : new Date(fin).getTime();
          if (finTs < now) return; // skip expired
        }
        const emisorNombre = getEmisorNombre(fianza);
        activeFianzas.push({
          ...fianza,
          _emisorNombre: emisorNombre ? emisorNombre.trim().toLowerCase() : '',
          _emisorId: fianza.emisor || '',
          clientId: contrato.clientId,
          contratoId: contrato.id,
          contratoTitle: contrato.tituloContrato?.value || 'Sin título',
        });
      });
    });

    // Match each client afianzadora by catalog name .includes() OR by emisor ID
    Object.values(clientAfianzadorasMap).flat().forEach((ca) => {
      const global = afianzadorasById[ca.afianzadoraId];
      if (!global?.nombre) { map[ca.id] = []; return; }
      const catalogName = global.nombre.trim().toLowerCase();
      map[ca.id] = activeFianzas.filter((f) =>
        (f._emisorNombre && f._emisorNombre.includes(catalogName)) ||
        (!f._emisorNombre && f._emisorId === ca.afianzadoraId)
      );
    });

    return map;
  }, [contractsList, clients, clientAfianzadorasMap, afianzadorasById]);

  // Flatten all client afianzadoras
  const allAfianzadoras = useMemo(() => {
    const clientIds = new Set(clients.map((c) => c.id));
    let result = [];
    Object.entries(clientAfianzadorasMap).forEach(([clientId, afs]) => {
      if (!clientIds.has(clientId)) return;
      afs.forEach((ca) => {
        const global = afianzadorasById[ca.afianzadoraId];
        const nombre = global?.nombre || 'Afianzadora desconocida';
        const logo = global?.logo || null;
        const linkedOS = (ca.obligadosSolidarios || []).map((id) => osMap[id]).filter(Boolean);
        const linkedBI = (ca.bienesInmuebles || []).map((r) => {
          const biKey = r.includes('/') ? r : `${clientId}/${r}`;
          return biMap[biKey];
        }).filter(Boolean);
        const activeFianzas = fianzasByAfianzadoraId[ca.id] || [];
        const totalAfianzado = activeFianzas.reduce((sum, f) => sum + (Number(f.montoAfianzado) || 0), 0);
        const linea = ca.lineaAfianzamiento || 0;
        const lineaDisponible = linea > 0 ? linea - totalAfianzado : null;
        const clientName = clients.find((c) => c.id === clientId)?.nombreEmpresa || 'Sin nombre';

        result.push({
          ...ca,
          nombre,
          logo,
          clientName,
          linkedOS,
          linkedBI,
          activeFianzas,
          totalAfianzado,
          linea,
          lineaDisponible,
        });
      });
    });
    return result;
  }, [clientAfianzadorasMap, afianzadorasById, osMap, biMap, fianzasByAfianzadoraId, clients]);

  if (clients.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
        <ShieldCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <Text className="text-gray-500 font-medium">No hay afianzadoras registradas.</Text>
      </div>
    );
  }

  if (afianzadorasLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  return (
    <>
      {allAfianzadoras.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <ShieldCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <Text className="text-gray-500 font-medium">No hay afianzadoras registradas.</Text>
        </div>
      ) : (
        <div className="space-y-4">
          {allAfianzadoras.map((af) => (
            <div key={`${af.clientId}-${af.id}`} className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
                {af.logo ? (
                  <img src={af.logo} alt={af.nombre} className="w-10 h-10 rounded-lg object-contain bg-zinc-50 border border-zinc-100 p-1" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-sm">
                    {af.nombre.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-zinc-900 truncate">{af.nombre}</h3>
                  {clients.length > 1 && (
                    <Text className="text-xs text-zinc-500">{af.clientName}</Text>
                  )}
                </div>
              </div>

              <div className="p-6">
                {/* Linea de Afianzamiento */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CurrencyDollarIcon className="w-4 h-4 text-zinc-400" />
                      <Text className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Línea de Afianzamiento</Text>
                    </div>
                    <div className="text-xl font-bold text-zinc-900">
                      {af.linea > 0 ? formatMXN(af.linea) : <span className="text-zinc-400 text-base">No definida</span>}
                    </div>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CurrencyDollarIcon className="w-4 h-4 text-zinc-400" />
                      <Text className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Línea Usada</Text>
                    </div>
                    <div className="text-xl font-bold text-zinc-700">
                      {formatMXN(af.totalAfianzado)}
                    </div>
                    <Text className="text-xs text-zinc-400 mt-0.5">{af.activeFianzas.length} fianza{af.activeFianzas.length !== 1 ? 's' : ''} activa{af.activeFianzas.length !== 1 ? 's' : ''}</Text>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CurrencyDollarIcon className="w-4 h-4 text-zinc-400" />
                      <Text className="text-xs text-zinc-500 uppercase tracking-wide font-medium">Disponible</Text>
                    </div>
                    <div className={`text-xl font-bold ${
                      af.lineaDisponible === null ? 'text-zinc-400'
                      : af.lineaDisponible < 0 ? 'text-red-600'
                      : 'text-emerald-600'
                    }`}>
                      {af.lineaDisponible !== null ? formatMXN(af.lineaDisponible) : '—'}
                    </div>
                    {af.linea > 0 && af.totalAfianzado > 0 && (
                      <div className="mt-2 h-2 bg-zinc-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${af.lineaDisponible < 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, (af.totalAfianzado / af.linea) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Active Fianzas */}
                {af.activeFianzas.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <DocumentTextIcon className="w-4 h-4 text-azul-surelia" />
                      <Text className="text-sm font-semibold text-zinc-800">Fianzas Activas</Text>
                      <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-azul-surelia/10 text-azul-surelia">
                        {af.activeFianzas.length}
                      </span>
                    </div>
                    <div className="space-y-1.5 sm:pl-6">
                      {af.activeFianzas.map((f, idx) => {
                        const monto = f.montoAfianzado || f.xmlFianza?.montoAfianzado || 0;
                        const numero = f.numeroFianza || f.xmlFianza?.numeroFianza || '';
                        const tipo = f.tipoFianza || f.xmlFianza?.tipo || f.tipo || '';
                        const inicio = f.inicioVigenciaFianza || f.xmlFianza?.inicioVigenciaFianza;
                        const fin = f.finVigenciaFianza || f.xmlFianza?.finVigenciaFianza;
                        const inicioDate = inicio ? new Date(typeof inicio === 'number' ? inicio : inicio).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                        const finDate = fin ? new Date(typeof fin === 'number' ? fin : fin).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                        return (
                          <button
                            key={f.id || idx}
                            onClick={() => { setSelectedFianza(f); setIsFianzaViewOpen(true); }}
                            className="w-full py-2.5 px-3 rounded-lg bg-zinc-50 hover:bg-azul-surelia/5 transition-colors text-left"
                          >
                            <div className="flex items-start gap-2">
                              <DocumentTextIcon className="w-4 h-4 text-azul-surelia flex-shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <span className="text-sm font-medium text-zinc-700 truncate block">
                                  {numero ? `#${numero}` : 'Fianza'}{tipo ? ` — ${tipo}` : ''}
                                </span>
                                <span className="text-xs text-zinc-400 block">
                                  {inicioDate && finDate ? `${inicioDate} — ${finDate}` : finDate ? `Vence: ${finDate}` : ''}
                                </span>
                                {f.contratoTitle && (
                                  <span className="text-xs text-zinc-400 block truncate">{f.contratoTitle}</span>
                                )}
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    f.pagada ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                  }`}>
                                    {f.pagada ? 'Pagada' : 'Pago pend.'}
                                  </span>
                                  <span className="text-sm font-semibold text-zinc-700">{formatMXN(monto)}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Obligados Solidarios */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserIcon className="w-4 h-4 text-purple-600" />
                    <Text className="text-sm font-semibold text-zinc-800">Obligados Solidarios</Text>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {af.linkedOS.length}
                    </span>
                  </div>
                  {af.linkedOS.length === 0 ? (
                    <Text className="text-xs text-zinc-400 italic sm:pl-6">Sin obligados solidarios registrados</Text>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:pl-6">
                      {af.linkedOS.map((os) => (
                        <div key={os.id} className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                          <UserIcon className="w-4 h-4 text-purple-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <Text className="text-sm font-medium text-zinc-800 truncate">{os.nombre || 'Sin nombre'}</Text>
                            {os.tipo && os.tipo !== 'cliente_existente' && (
                              <Text className="text-xs text-zinc-500">
                                {os.tipo === 'persona_fisica' ? 'Persona Física'
                                  : os.tipo === 'persona_moral' ? 'Persona Moral'
                                  : os.tipo}
                              </Text>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bienes Inmuebles en Garantía */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <HomeModernIcon className="w-4 h-4 text-amber-600" />
                    <Text className="text-sm font-semibold text-zinc-800">BI en Garantía</Text>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      {af.linkedBI.length}
                    </span>
                  </div>
                  {af.linkedBI.length === 0 ? (
                    <Text className="text-xs text-zinc-400 italic sm:pl-6">Sin bienes inmuebles en garantía</Text>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:pl-6">
                      {af.linkedBI.map((bi) => (
                        <div key={bi.id} className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                          <HomeModernIcon className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <Text className="text-sm font-medium text-zinc-800 truncate">{bi.descripcion || 'Sin descripción'}</Text>
                            {bi.ownerNombre && (
                              <Text className="text-xs text-zinc-500">Propietario: {bi.ownerNombre}</Text>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FianzaView Modal — rendered via portal to avoid container clipping */}
      {selectedFianza && ReactDOM.createPortal(
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => { setIsFianzaViewOpen(false); setSelectedFianza(null); }}
          fianza={selectedFianza}
          clientData={clientsById[selectedFianza.clientId]}
          contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />,
        document.body
      )}
    </>
  );
};

export default PortalAfianzadorasList;
